import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Shield, Loader2 } from 'lucide-react';
import { useRoles } from '@/hooks/roles';
import { useHasPermission } from '@/hooks/permissions';
import type { Role } from '@/types/roles';

interface RoleSelectorProps {
  currentRoleId: string | null;
  currentRoleName: string | null;
  onRoleChange: (roleId: string, roleName: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * Role selector component for assigning roles to users.
 * Only renders if the current user has the manage_employee_roles permission.
 */
export const RoleSelector = ({
  currentRoleId,
  currentRoleName,
  onRoleChange,
  disabled = false,
  isLoading = false,
}: RoleSelectorProps) => {
  const canManageRoles = useHasPermission('manage_employee_roles');
  const { data: roles, isLoading: rolesLoading } = useRoles();

  // Don't render if user doesn't have permission
  if (!canManageRoles) {
    return null;
  }

  const handleValueChange = (roleId: string) => {
    const selectedRole = roles?.find((r: Role) => r.id === roleId);
    if (selectedRole) {
      onRoleChange(roleId, selectedRole.name);
    }
  };

  // Determine the display value
  const displayValue = currentRoleId || '';
  const displayLabel = currentRoleName || 'Select a role';

  return (
    <div className="space-y-2">
      <Label htmlFor="role-select" className="flex items-center gap-2">
        <Shield className="h-4 w-4" />
        Role
      </Label>
      <Select
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled || rolesLoading || isLoading}
      >
        <SelectTrigger id="role-select" className="w-full">
          {rolesLoading || isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : (
            <SelectValue placeholder={displayLabel} />
          )}
        </SelectTrigger>
        <SelectContent>
          {roles?.map((role: Role) => (
            <SelectItem key={role.id} value={role.id}>
              <div className="flex items-center gap-2">
                <span>{role.name}</span>
                {role.is_system && (
                  <span className="text-xs text-muted-foreground">(System)</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Role changes typically take effect within a few seconds.
      </p>
    </div>
  );
};
